/**
 * Secure Invite Code Validation
 * Uses SHA-256 hashing to prevent exposing valid codes in devtools
 * 
 * Phase 1: Hard-coded hashes for beta access (current)
 * Phase 2: Server-side validation with ed25519 key association
 * Phase 3: Full KYC integration
 */

/**
 * Hash an invite code using SHA-256
 * Codes are normalized to uppercase and trimmed
 */
export async function hashInviteCode(code: string): Promise<string> {
  const encoder = new TextEncoder();
  const normalizedCode = code.toUpperCase().trim();
  const data = encoder.encode(normalizedCode);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Pre-computed SHA-256 hashes of valid 22-character invite codes
 * These are the only codes that will grant access to the beta
 * 
 * To generate: 
 * 1. Create 22-char codes
 * 2. Hash each with hashInviteCode()
 * 3. Store only the hashes here
 * 
 * Example for testing: "TESTCODE1234567890ABCD" -> "2e72f6c6e4d4e1c8f1b2a3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4"
 */
const VALID_INVITE_HASHES = new Set([
  // Test code for development (remove in production)
  '2e99758548972a8e8822ad47fa1017ff72f06f3ff6a016851f45c398732bc50c', // "TESTCODE1234567890ABCD"

  // Production invite codes (20 codes for beta users)
  '2448e9cb7c945a201927fd1ca074219f396794d38cf15596a8529b64a5c00ea9', // "1MOBYCHAT2025INVITE111"
  '086b593398de4400b798a6a6396da35cf61551c59258018fb1769a9c78bb3681', // "1MOBYCHAT2025INVITE222"
  'a8af5099c3d4d3aabceb8cb17e23d83f4377aa325ce1933551734a68a1296fc1', // "1MOBYCHAT2025INVITE333"
  '429e340949e1cb5bd71b7d822b68031da0145fee62e68ecde4f7d50dc2c3ba58', // "1MOBYCHAT2025INVITE444"
  '02f612486ec6aa2c20a2add8a6344aea67a65d204febcad2d3581353a81b9863', // "1MOBYCHAT2025INVITE555"
  'e139c55347d6e96ca8615dbb4434b8abab7b0334bf7ee6ea572bfe4dd4a31e42', // "1MOBYCHAT2025INVITE666"
  'be66f96cf7660a602fe1232672c5969d005ddaee192236a3efb8344b5a8c316c', // "1MOBYCHAT2025INVITE777"
  '5489d19c337f9cd4d9b9bf31d45aa6aa8fe22ab18ecc4b1eea4cbaab97f2eed6', // "1MOBYCHAT2025INVITE888"
  '9ae48aa55579007177d022dbafbe2f539e07a59d3bea3ee748e64c5820bb522b', // "1MOBYCHAT2025INVITE999"
  '86e511bc73ed9701c156c7863e00aaab3f3042e9964993b52db4f7f901f2225d', // "1MOBYCHAT2025INVITE000"
  'e69f90b539fe9aa6268cd3abd45696671fb4dba721706b79861c6e3d945c36cf', // "9MOBYCHAT2025INVITE111"
  'd08d1225b1ae5d2f434368c4e98671c646dbe186ec01bba435bbb0de812ac4a3', // "9MOBYCHAT2025INVITE222"
  'a148336503b4f6d09610fa939a03c433d55aa088b8be2f2248877bb0bda345e3', // "9MOBYCHAT2025INVITE333"
  '693c3e14b91088f79f822d6c06ef43868522c79f39dd2e8219cccd7906b1020d', // "9MOBYCHAT2025INVITE444"
  'e494700fac6ab939ca268aee47b57fca7c1ee8a8c6ce6bf3e0be5d5c527eb605', // "9MOBYCHAT2025INVITE555"
  '478e519d91252159a8c75f9fa9ae15c515dfc3ff9b193473638a4d81271b157a', // "9MOBYCHAT2025INVITE666"
  'c4b65012a508a16340cf60f469b88f838e2d130f651175c14faac2af1d5a935a', // "9MOBYCHAT2025INVITE777"
  '4ef580dd2dcf1754736d7965f9cf3c32bb4630e8ef38510809523a5d138d71d9', // "9MOBYCHAT2025INVITE888"
  'cb4795d499797caed901fcbb8f49f3ee78a2e714bb941afa88237488e332221b', // "9MOBYCHAT2025INVITE999"
  '0fb9bcf2da025a29ea00927c4ca7a5b57c33be55cbc5da2499b93f1c060e8170', // "9MOBYCHAT2025INVITE000"
]);

/**
 * Validate an invite code against the hash list
 * Returns true if valid, false otherwise
 */
export async function validateInviteCode(code: string): Promise<boolean> {
  // Check basic format
  if (!code || code.trim().length !== 22) {
    return false;
  }
  
  try {
    const codeHash = await hashInviteCode(code);
    return VALID_INVITE_HASHES.has(codeHash);
  } catch (error) {
    console.error('[INVITE] Validation error:', error);
    return false;
  }
}

/**
 * Store validated invite for future server sync
 * This will be used in Phase 2 to associate with user's ed25519 key
 */
export function storeValidatedInvite(codeHash: string): void {
  localStorage.setItem('moby_invite_validated', 'true');
  localStorage.setItem('moby_invite_hash', codeHash);
  localStorage.setItem('moby_invite_timestamp', Date.now().toString());
}

/**
 * Check if user has already validated an invite code
 */
export function hasValidatedInvite(): boolean {
  return localStorage.getItem('moby_invite_validated') === 'true';
}

/**
 * Get stored invite information for server sync
 * Returns null if no valid invite stored
 */
export function getStoredInviteInfo(): {
  hash: string;
  timestamp: number;
} | null {
  const validated = localStorage.getItem('moby_invite_validated');
  const hash = localStorage.getItem('moby_invite_hash');
  const timestamp = localStorage.getItem('moby_invite_timestamp');
  
  if (validated === 'true' && hash && timestamp) {
    return {
      hash,
      timestamp: parseInt(timestamp, 10)
    };
  }
  
  return null;
}
